import { useCallback, useEffect, useMemo, useState } from "react";
import { getAdminData, postAdminData } from "../../apis";
import { API, adminEventActivatePath } from "../../apis/config";
import { AdminEventListItem } from "../../interfaces/AdminEvent";
import { eventDisplayLabel } from "../../utils/eventDisplayName";

export default function EventSearchActivate() {
  const [query, setQuery] = useState("");
  const [listFilter, setListFilter] = useState("");
  const [results, setResults] = useState<AdminEventListItem[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [searching, setSearching] = useState(false);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const filteredResults = useMemo(() => {
    const q = listFilter.trim().toLowerCase();
    if (!q) return results;
    return results.filter((item) => item.name.includes(q));
  }, [results, listFilter]);

  const fetchEventList = useCallback(async (nameFilter: string) => {
    const trimmed = nameFilter.trim();
    setSearching(true);
    setError(null);
    setMessage(null);
    try {
      const res = await getAdminData<AdminEventListItem[]>(API.ADMIN.EVENTS, {
        ...(trimmed ? { params: { name: trimmed } } : {}),
      });
      if (!res.isSuccess || !Array.isArray(res.result)) {
        setResults([]);
        setError(res.message || "행사 조회에 실패했습니다.");
        return;
      }
      const list = res.result;
      const matched = trimmed
        ? list.filter((item) =>
            item.name.toLowerCase().includes(trimmed.toLowerCase()),
          )
        : list;
      setResults(matched);
      if (trimmed && matched.length === 0) {
        setMessage("조건에 맞는 행사가 없습니다.");
      }
    } catch (err: unknown) {
      setResults([]);
      setError(
        err instanceof Error
          ? err.message
          : "행사 조회 중 오류가 발생했습니다.",
      );
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    void fetchEventList("");
  }, [fetchEventList]);

  const handleSearch = () => {
    setSelectedId("");
    setListFilter("");
    void fetchEventList(query);
  };

  const handleActivate = async () => {
    setMessage(null);
    setError(null);
    const id = selectedId ? Number(selectedId) : NaN;
    if (!Number.isFinite(id)) {
      setError("행사를 선택해 주세요.");
      return;
    }
    setActivating(true);
    try {
      const res = await postAdminData<unknown>(adminEventActivatePath(id), {});
      if (!res.isSuccess) {
        setError(res.message || "행사 활성화에 실패했습니다.");
        return;
      }
      setSelectedId("");
      setQuery("");
      await fetchEventList("");
      setMessage("행사가 활성화되었습니다.");
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "행사 활성화 요청 중 오류가 발생했습니다.",
      );
    } finally {
      setActivating(false);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm text-left">
      <h3 className="text-lg font-semibold text-slate-900 mb-1">
        행사 조회 · 활성화
      </h3>
      <p className="text-sm text-slate-500 mb-4">
        검색 후, 활성화할 행을 선택하여 행사 활성화 버튼을 눌러주세요
      </p>

      <div className="max-w-5xl space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1">
            <label
              htmlFor="admin-event-search"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              검색
            </label>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
              <input
                id="admin-event-search"
                type="search"
                autoComplete="off"
                spellCheck={false}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  if (message === "조건에 맞는 행사가 없습니다.") {
                    setMessage(null);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                placeholder="행사명 부분 검색 가능"
                className="min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
              />
              <button
                type="button"
                onClick={handleSearch}
                disabled={searching}
                className="shrink-0 rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-60 sm:self-stretch sm:min-h-[42px]"
              >
                {searching ? "조회 중…" : "조회"}
              </button>
            </div>
          </div>
          <div className="flex w-full shrink-0 flex-col sm:w-auto sm:justify-end">
            <div className="mb-1 hidden h-5 sm:block" aria-hidden />
            <button
              type="button"
              onClick={() => void handleActivate()}
              disabled={activating || !selectedId}
              className="min-h-[42px] w-full rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              {activating ? "처리 중…" : "행사 활성화"}
            </button>
          </div>
        </div>

        <div>
          <div className="mb-1 text-sm font-medium text-slate-700">
            행사 리스트
          </div>
          <div
            className="max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-slate-50/80"
            aria-label="행사 목록"
          >
            {searching && results.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-slate-500">
                목록 불러오는 중…
              </p>
            ) : results.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-slate-500">
                표시할 행사가 없습니다
              </p>
            ) : filteredResults.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-slate-500">
                목록 필터와 일치하는 행사가 없습니다
              </p>
            ) : (
              <ul className="divide-y divide-slate-200 py-1">
                {filteredResults.map((item) => {
                  const idStr = String(item.id);
                  const selected = selectedId === idStr;
                  const label = eventDisplayLabel(
                    item.name,
                    item.startAt,
                    item.endAt,
                  );
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        aria-pressed={selected}
                        onClick={() => setSelectedId(idStr)}
                        className={`flex w-full items-center px-3 py-2.5 text-left text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-inset ${
                          selected
                            ? "bg-emerald-100 text-emerald-950"
                            : "bg-white text-slate-900 hover:bg-slate-100"
                        }`}
                      >
                        <span className="min-w-0 flex-1 truncate">{label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {error && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      {message && (
        <p className="mt-3 text-sm text-emerald-700" role="status">
          {message}
        </p>
      )}
    </section>
  );
}
